import React from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { eyebrowSx, heroPanelSx, pageHeadlineSx, sectionCardSx, subtleCopySx, warmPanelSx } from './designSystem';

const sections = [
  {
    title: 'Acceptance of Terms',
    body: 'By accessing or using BlockWebAI, you agree to these Terms of Service. If you are using the product on behalf of a school, district, company, or other organization, you represent that you have authority to bind that organization to these terms.',
  },
  {
    title: 'Service Description',
    body: 'BlockWebAI provides software and related services for policy-driven web filtering, page review, reporting, device enrollment, and administrative management. Features may change over time, and some functionality may depend on third-party platforms, browsers, or infrastructure.',
  },
  {
    title: 'Accounts and Access',
    body: 'You are responsible for maintaining the confidentiality of your account credentials and for activity that occurs under your account. You must provide accurate information and keep it reasonably up to date. We may suspend or terminate access if an account is used unlawfully, abusively, or in a way that threatens the service or other users.',
  },
  {
    title: 'Acceptable Use',
    body: 'You may not use the service to violate law, infringe rights, interfere with other users, attempt unauthorized access, reverse engineer protected parts of the product beyond what law permits, or deploy the service in a deceptive or harmful way. You are also responsible for ensuring your own policies and deployments comply with applicable school, workplace, and regulatory requirements.',
  },
  {
    title: 'School Deployments',
    body: 'If you deploy BlockWebAI for a school or educational organization, you are responsible for determining whether the service is appropriate for your environment, obtaining any required permissions or notices, and configuring the product in a lawful and institutionally appropriate way.',
  },
  {
    title: 'Fees and Paid Plans',
    body: 'If you purchase a paid plan, you agree to the pricing, billing terms, and plan limits presented at the time of purchase or in a separate agreement. Failure to pay may result in downgrade, suspension, or termination of paid features.',
  },
  {
    title: 'Third-Party Services',
    body: 'The service may rely on third-party providers for hosting, email, analytics, AI processing, identity, browser distribution, or related infrastructure. We are not responsible for outages or changes caused by third-party services outside our reasonable control.',
  },
  {
    title: 'Disclaimers',
    body: 'The service is provided on an "as is" and "as available" basis, without warranties of any kind except as required by law. We do not guarantee uninterrupted operation, perfect accuracy, or that every harmful, evasive, or distracting page will always be detected or blocked.',
  },
  {
    title: 'Limitation of Liability',
    body: 'To the maximum extent permitted by law, BlockWebAI and its operators will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of data, profits, goodwill, or business interruption arising from or related to the service.',
  },
  {
    title: 'Termination',
    body: 'You may stop using the service at any time. We may suspend or terminate access when reasonably necessary to protect the service, comply with law, enforce these terms, or address nonpayment, abuse, or security concerns.',
  },
  {
    title: 'Changes',
    body: 'We may update these terms from time to time. Continued use of the service after an update becomes effective constitutes acceptance of the revised terms, unless applicable law requires a different process.',
  },
  {
    title: 'Contact',
    body: 'Questions about these terms can be sent to jonathanlarsonbusiness@gmail.com.',
  },
];

function TermsOfService() {
  return (
    <Container sx={{ py: { xs: 6, md: 8 }, maxWidth: '980px !important' }}>
      <Stack spacing={4}>
        <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 5 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={eyebrowSx}>
                Terms of Service
              </Typography>
              <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1 }}>
                Terms for using BlockWebAI
              </Typography>
              <Typography sx={{ ...subtleCopySx, mt: 2, lineHeight: 1.85 }}>
                These terms govern access to and use of the BlockWebAI website, dashboard, extension, and related
                services.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ ...warmPanelSx, p: 3 }}>
                <Typography variant="subtitle2" sx={{ color: '#8C6B1F', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                  Effective Date
                </Typography>
                <Typography sx={{ mt: 1.2, color: '#52677A', lineHeight: 1.75, fontWeight: 600 }}>
                  April 18, 2026
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Stack spacing={2.25}>
          {sections.map((section, index) => (
            <Box key={section.title} sx={{ ...sectionCardSx, p: { xs: 3, md: 3.5 } }}>
              <Stack direction="row" spacing={1.75} alignItems="baseline">
                <Typography
                  sx={{
                    color: '#13947F',
                    fontWeight: 800,
                    fontSize: '0.78rem',
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Box>
                  <Typography variant="h5" sx={{ color: '#0B2E43', mb: 1.2 }}>
                    {section.title}
                  </Typography>
                  <Typography sx={{ ...subtleCopySx, lineHeight: 1.8 }}>{section.body}</Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

export default TermsOfService;
